import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  Chapter,
  ChapterVersion,
  Note,
  Todo,
  CreateChapterRequest,
  UpdateChapterRequest,
  SaveChapterContentRequest,
  CreateNoteRequest,
  CreateTodoRequest,
  APIError
} from '../../types/api';
import { apiClient } from '../../services/api';
import type { RootState } from '../index';

interface ChapterState {
  chapters: Chapter[];
  currentChapter: Chapter | null;
  currentVersion: ChapterVersion | null;
  versions: ChapterVersion[];
  notes: Note[];
  todos: Todo[];
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isSaving: boolean;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  lastSaved: string | null;
  error: string | null;
}

const initialState: ChapterState = {
  chapters: [],
  currentChapter: null,
  currentVersion: null,
  versions: [],
  notes: [],
  todos: [],
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isSaving: false,
  saveStatus: 'idle',
  lastSaved: null,
  error: null,
};

// Async thunks
export const fetchChapters = createAsyncThunk<
  Chapter[],
  string,
  { rejectValue: APIError }
>(
  'chapter/fetchChapters',
  async (memoirId, { rejectWithValue }) => {
    try {
      const chapters = await apiClient.getChapters(memoirId);
      return chapters;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const fetchChapter = createAsyncThunk<
  Chapter,
  string,
  { rejectValue: APIError }
>(
  'chapter/fetchChapter',
  async (chapterId, { rejectWithValue }) => {
    try {
      const chapter = await apiClient.getChapter(chapterId);
      return chapter;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const createChapter = createAsyncThunk<
  Chapter,
  { memoirId: string; data: CreateChapterRequest },
  { rejectValue: APIError }
>(
  'chapter/createChapter',
  async ({ memoirId, data }, { rejectWithValue }) => {
    try {
      const chapter = await apiClient.createChapter(memoirId, data);
      return chapter;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const updateChapter = createAsyncThunk<
  Chapter,
  { chapterId: string; data: UpdateChapterRequest },
  { rejectValue: APIError }
>(
  'chapter/updateChapter',
  async ({ chapterId, data }, { rejectWithValue }) => {
    try {
      const chapter = await apiClient.updateChapter(chapterId, data);
      return chapter;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const deleteChapter = createAsyncThunk<
  string,
  string,
  { rejectValue: APIError }
>(
  'chapter/deleteChapter',
  async (chapterId, { rejectWithValue }) => {
    try {
      await apiClient.deleteChapter(chapterId);
      return chapterId;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const saveChapterContent = createAsyncThunk<
  ChapterVersion,
  { chapterId: string; data: SaveChapterContentRequest },
  { rejectValue: APIError }
>(
  'chapter/saveChapterContent',
  async ({ chapterId, data }, { rejectWithValue }) => {
    try {
      const version = await apiClient.saveChapterContent(chapterId, data);
      return version;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const fetchChapterVersions = createAsyncThunk<
  ChapterVersion[],
  string,
  { rejectValue: APIError }
>(
  'chapter/fetchChapterVersions',
  async (chapterId, { rejectWithValue }) => {
    try {
      const versions = await apiClient.getChapterVersions(chapterId);
      return versions;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const fetchChapterVersion = createAsyncThunk<
  ChapterVersion,
  string,
  { rejectValue: APIError }
>(
  'chapter/fetchChapterVersion',
  async (versionId, { rejectWithValue }) => {
    try {
      const version = await apiClient.getChapterVersion(versionId);
      return version;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const reorderChapters = createAsyncThunk<
  Chapter[],
  { memoirId: string; chapterIds: string[] },
  { rejectValue: APIError }
>(
  'chapter/reorderChapters',
  async ({ memoirId, chapterIds }, { rejectWithValue }) => {
    try {
      const chapters = await apiClient.reorderChapters(memoirId, chapterIds);
      return chapters;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

// Notes thunks
export const fetchNotes = createAsyncThunk<
  Note[],
  string,
  { rejectValue: APIError }
>(
  'chapter/fetchNotes',
  async (chapterId, { rejectWithValue }) => {
    try {
      const notes = await apiClient.getNotes(chapterId);
      return notes;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const createNote = createAsyncThunk<
  Note,
  { chapterId: string; data: CreateNoteRequest },
  { rejectValue: APIError }
>(
  'chapter/createNote',
  async ({ chapterId, data }, { rejectWithValue }) => {
    try {
      const note = await apiClient.createNote(chapterId, data);
      return note;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const updateNote = createAsyncThunk<
  Note,
  { noteId: string; data: Partial<CreateNoteRequest> },
  { rejectValue: APIError }
>(
  'chapter/updateNote',
  async ({ noteId, data }, { rejectWithValue }) => {
    try {
      const note = await apiClient.updateNote(noteId, data);
      return note;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const deleteNote = createAsyncThunk<
  string,
  string,
  { rejectValue: APIError }
>(
  'chapter/deleteNote',
  async (noteId, { rejectWithValue }) => {
    try {
      await apiClient.deleteNote(noteId);
      return noteId;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

// Todos thunks
export const fetchTodos = createAsyncThunk<
  Todo[],
  string,
  { rejectValue: APIError }
>(
  'chapter/fetchTodos',
  async (chapterId, { rejectWithValue }) => {
    try {
      const todos = await apiClient.getTodos(chapterId);
      return todos;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const createTodo = createAsyncThunk<
  Todo,
  { chapterId: string; data: CreateTodoRequest },
  { rejectValue: APIError }
>(
  'chapter/createTodo',
  async ({ chapterId, data }, { rejectWithValue }) => {
    try {
      const todo = await apiClient.createTodo(chapterId, data);
      return todo;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const updateTodo = createAsyncThunk<
  Todo,
  { todoId: string; data: Partial<CreateTodoRequest & { is_completed: boolean }> },
  { rejectValue: APIError }
>(
  'chapter/updateTodo',
  async ({ todoId, data }, { rejectWithValue }) => {
    try {
      const todo = await apiClient.updateTodo(todoId, data);
      return todo;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const deleteTodo = createAsyncThunk<
  string,
  string,
  { rejectValue: APIError }
>(
  'chapter/deleteTodo',
  async (todoId, { rejectWithValue }) => {
    try {
      await apiClient.deleteTodo(todoId);
      return todoId;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

const chapterSlice = createSlice({
  name: 'chapter',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentChapter: (state, action: PayloadAction<Chapter | null>) => {
      state.currentChapter = action.payload;
    },
    setCurrentVersion: (state, action: PayloadAction<ChapterVersion | null>) => {
      state.currentVersion = action.payload;
    },
    setSaveStatus: (state, action: PayloadAction<'idle' | 'saving' | 'saved' | 'error'>) => {
      state.saveStatus = action.payload;
      if (action.payload === 'saved') {
        state.lastSaved = new Date().toISOString();
      }
    },
    updateChapterOrder: (state, action: PayloadAction<Chapter[]>) => {
      state.chapters = action.payload;
    },
    clearChapterData: (state) => {
      state.chapters = [];
      state.currentChapter = null;
      state.currentVersion = null;
      state.versions = [];
      state.notes = [];
      state.todos = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Chapters
      .addCase(fetchChapters.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchChapters.fulfilled, (state, action) => {
        state.isLoading = false;
        state.chapters = action.payload;
        state.error = null;
      })
      .addCase(fetchChapters.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || 'Failed to fetch chapters';
      })
      // Fetch Chapter
      .addCase(fetchChapter.fulfilled, (state, action) => {
        state.currentChapter = action.payload;
        // Update chapter in list if it exists
        const index = state.chapters.findIndex(c => c.chapter_id === action.payload.chapter_id);
        if (index !== -1) {
          state.chapters[index] = action.payload;
        }
      })
      // Create Chapter
      .addCase(createChapter.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createChapter.fulfilled, (state, action) => {
        state.isCreating = false;
        state.chapters.push(action.payload);
        state.error = null;
      })
      .addCase(createChapter.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload?.detail || 'Failed to create chapter';
      })
      // Update Chapter
      .addCase(updateChapter.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateChapter.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.chapters.findIndex(c => c.chapter_id === action.payload.chapter_id);
        if (index !== -1) {
          state.chapters[index] = action.payload;
        }
        if (state.currentChapter?.chapter_id === action.payload.chapter_id) {
          state.currentChapter = action.payload;
        }
        state.error = null;
      })
      .addCase(updateChapter.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload?.detail || 'Failed to update chapter';
      })
      // Delete Chapter
      .addCase(deleteChapter.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteChapter.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.chapters = state.chapters.filter(c => c.chapter_id !== action.payload);
        if (state.currentChapter?.chapter_id === action.payload) {
          state.currentChapter = null;
          state.currentVersion = null;
        }
        state.error = null;
      })
      .addCase(deleteChapter.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload?.detail || 'Failed to delete chapter';
      })
      // Save Chapter Content
      .addCase(saveChapterContent.pending, (state) => {
        state.isSaving = true;
        state.saveStatus = 'saving';
        state.error = null;
      })
      .addCase(saveChapterContent.fulfilled, (state, action) => {
        state.isSaving = false;
        state.saveStatus = 'saved';
        state.lastSaved = new Date().toISOString();
        state.currentVersion = action.payload;

        // Update version in list
        const index = state.versions.findIndex(v => v.version_id === action.payload.version_id);
        if (index !== -1) {
          state.versions[index] = action.payload;
        } else {
          state.versions.unshift(action.payload);
        }

        // Update chapter word count
        if (state.currentChapter) {
          state.currentChapter.word_count = action.payload.word_count;

          // Also update the chapter in the chapters array for left panel
          const chapterIndex = state.chapters.findIndex(
            c => c.chapter_id === state.currentChapter!.chapter_id
          );
          if (chapterIndex !== -1) {
            state.chapters[chapterIndex].word_count = action.payload.word_count;
          }
        }

        state.error = null;
      })
      .addCase(saveChapterContent.rejected, (state, action) => {
        state.isSaving = false;
        state.saveStatus = 'error';
        state.error = action.payload?.detail || 'Failed to save chapter content';
      })
      // Fetch Chapter Versions
      .addCase(fetchChapterVersions.fulfilled, (state, action) => {
        state.versions = action.payload;
      })
      // Fetch Chapter Version
      .addCase(fetchChapterVersion.fulfilled, (state, action) => {
        state.currentVersion = action.payload;
      })
      // Reorder Chapters
      .addCase(reorderChapters.fulfilled, (state, action) => {
        state.chapters = action.payload;
      })
      // Notes
      .addCase(fetchNotes.fulfilled, (state, action) => {
        state.notes = action.payload;
      })
      .addCase(createNote.fulfilled, (state, action) => {
        state.notes.push(action.payload);
      })
      .addCase(updateNote.fulfilled, (state, action) => {
        const index = state.notes.findIndex(n => n.note_id === action.payload.note_id);
        if (index !== -1) {
          state.notes[index] = action.payload;
        }
      })
      .addCase(deleteNote.fulfilled, (state, action) => {
        state.notes = state.notes.filter(n => n.note_id !== action.payload);
      })
      // Todos
      .addCase(fetchTodos.fulfilled, (state, action) => {
        state.todos = action.payload;
      })
      .addCase(createTodo.fulfilled, (state, action) => {
        state.todos.push(action.payload);
      })
      .addCase(updateTodo.fulfilled, (state, action) => {
        const index = state.todos.findIndex(t => t.todo_id === action.payload.todo_id);
        if (index !== -1) {
          state.todos[index] = action.payload;
        }
      })
      .addCase(deleteTodo.fulfilled, (state, action) => {
        state.todos = state.todos.filter(t => t.todo_id !== action.payload);
      });
  },
});

export const {
  clearError,
  setCurrentChapter,
  setCurrentVersion,
  setSaveStatus,
  updateChapterOrder,
  clearChapterData,
} = chapterSlice.actions;

export default chapterSlice.reducer;

// Selectors
export const selectChapters = (state: RootState) => state.chapter?.chapters || [];
export const selectCurrentChapter = (state: RootState) => state.chapter?.currentChapter || null;
export const selectCurrentVersion = (state: RootState) => state.chapter?.currentVersion || null;
export const selectChapterVersions = (state: RootState) => state.chapter?.versions || [];
export const selectChapterNotes = (state: RootState) => state.chapter?.notes || [];
export const selectChapterTodos = (state: RootState) => state.chapter?.todos || [];
export const selectChapterLoading = (state: RootState) => state.chapter?.isLoading || false;
export const selectChapterSaving = (state: RootState) => state.chapter?.isSaving || false;
export const selectSaveStatus = (state: RootState) => state.chapter?.saveStatus || 'idle';
export const selectLastSaved = (state: RootState) => state.chapter?.lastSaved || null;
export const selectChapterError = (state: RootState) => state.chapter?.error || null;